import styled, { useTheme, keyframes } from 'styled-components';
import Highcharts from 'highcharts/highcharts-gantt';
import HighchartsReact from 'highcharts-react-official';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACdetails, CustomReportsDetails, useCampaignContext } from 'context/CampaignContext';

import { useGetGanttOptions } from 'customHooks/useGanttOptions';
import { a, API } from 'aws-amplify';
import { deleteCustomReport, saveCustomReport, updateCustomReport } from 'graphql/mutations';
import {
  ClientInputUmbrella,
  CustomReportResponse,
  datatype,
  DeleteCustomReportMutation,
  DeleteCustomReportMutationVariables,
  filtertype,
  GetCustomReportQuery,
  GetCustomReportQueryVariables,
  SaveCustomReportMutation,
  SaveCustomReportMutationVariables,
  UpdateCustomReportMutation,
  UpdateCustomReportMutationVariables,
} from 'API';
import { getCustomReport } from 'graphql/queries';
import { set } from 'lodash';
import { IActiveOrganization, useClientContext } from 'context/ClientContext';
import { CampaignPeriodValues } from '../AddCampaignPeriodModal';

// CustomEvents(Highcharts);

export const OrganisationReportStyled = styled.div`
  width: calc(100vw - 4.5vw);
  padding: 10px;
  grid-column: span 3;
  background-color: ${({ theme }) => theme.placeholderBackground};
  border: 1px solid ${({ theme }) => theme.border};
  z-index: 0;
  /* overflow-x: scroll; */
  /* overflow-y: auto; */
  max-height: 800px;

  path {
    stroke: ${({ theme }) => theme.border};
  }
  .highcharts-xaxis-grid {
    path:not(:last-child) {
      stroke: none;
    }
  }
`;

type Props = {};

export const SkeletonPulse = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -200% 0; }
`;

export const Skeleton = styled.div`
  animation: ${SkeletonPulse} 1.5s ease-in-out infinite;
  background: linear-gradient(-90deg, #f0f0f0 0%, #f8f8f8 50%, #f0f0f0 100%);
  background-size: 200% 100%;
  border-radius: 4px;
  display: inline-block;
  line-height: 1;
  width: 100%;
`;

const OrganisationReport = (props: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { anyPublish } = useClientContext();
  const { customReportsList, reducedClientList } = useCampaignContext();
  const [options, setOptions] = useState<any>('');
  const [series, setSeries] = useState<any[] | null>(null);
  const [o] = useGetGanttOptions(series);
  const [seter, setSeter] = useState<any | null>(null);

  // {
  //   column: 'date',
  //   filter: {
  //     filtertype: filtertype.BETWEEN,
  //     datatype: datatype.DATE,
  //     from: '2023-10-25',
  //     to: '2023-11-02',
  //   },
  // },

  const handleClick = useCallback(
    (type: string, campaignId: number) => {
      const foundReport = customReportsList?.find((report) => report?.CustomReportID === campaignId);
      if (foundReport) {
        searchParams.set('customReportID', campaignId.toString());
        searchParams.set('tab', type);
        setSearchParams(searchParams);
      }
    },
    [customReportsList, searchParams, setSearchParams],
  );

  useEffect(() => {
    // Use setTimeout to defer heavy processing and prevent blocking
    const timeoutId = setTimeout(() => {
      let seriesArray = [] as any[];
      const groupedPerClientCode =
        customReportsList !== undefined &&
        customReportsList?.length > 0 &&
        customReportsList.reduce(
          (result: any, item: any) => ({
            ...result,
            [item.Client['Code']]: [...(result[item.Client['Code']] || []), item],
          }),
          {},
        );

      if (groupedPerClientCode) {
        for (const [index, [key, value]] of Object.entries(groupedPerClientCode).entries()) {
          const ran = Math.random().toString(36).substring(7);
          // Find client name from reducedClientList
          const clientFromList = reducedClientList?.find((client) => client?.ClientCode === key);
          const displayName = clientFromList?.ClientName ? `${clientFromList.ClientName} (${key})` : key;

          // CREATE PARENT DATA AND ADD THEM TO A SERIES ARRAY
          seriesArray.push({
            name: displayName,
            id: key + ran,
            pointWidth: 3,
          });
          // CREATE CHILD DATA AND ADD THEM TO A SERIES ARRAY
          const childData = (value as [])
            .map((element: CustomReportsDetails) => {
              if (element) {
                const startDate = element.StartDate;
                const endDate = element.EndDate;
                return {
                  parent: key + ran,
                  borderColor: 'transparent',
                  name: element.CustomReportName,
                  start: new Date(startDate as string).getTime(),
                  end: new Date(endDate as string).getTime(),
                  events: {
                    click: function (e: any) {
                      handleClick('customReport', element.CustomReportID);
                    },
                  },
                  dataLabels: {
                    enabled: true,
                    useHTML: true,
                    style: {
                      color: '#000000',
                      fontWeight: 'normal',
                      textOutline: 'none',
                    },
                  },
                  color:
                    Date.now() < new Date(startDate as string).getTime()
                      ? '#BFBFBF'
                      : Date.now() > new Date(startDate as string).getTime() &&
                          Date.now() < new Date(endDate as string).getTime() //IF THE CAMPAIGN IS ACTIVE
                        ? '#A9C8C0'
                        : Date.now() > new Date(endDate as string).getTime() && //IF THE CAMPAIGN IS COMPLETED
                            element.subcampaign[-1].published.DIGITAL === true
                          ? '#AFA2C3'
                          : Date.now() > new Date(endDate as string).getTime() && //IF THE CAMPAIGN IS COMPLETED
                              element.subcampaign[-1].published.DIGITAL === false
                            ? '#7D98AB'
                            : '',
                };
              }
              return null;
            })
            .filter(Boolean);

          seriesArray.push(...childData);
        }
        setSeter(seriesArray);
        // setSeries([]);
        // setSeries(null)
      } else {
        setSeter(null);
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [customReportsList, handleClick]);

  useEffect(() => {
    if (seter !== null) {
      setSeries(seter);
    } else {
      setSeries([]);
    }
  }, [seter]);

  // const [addCustomReport] = useSaveCustomReports();

  return (
    <OrganisationReportStyled>
      {/* Beta Badge */}
      <div
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '6px',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          color: '#856404',
          maxWidth: '400px',
          zIndex: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <span
          style={{
            backgroundColor: '#fd79a8',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        >
          Beta
        </span>
        <span>Beta release: This feature is under testing and may not yet be fully stable. Feedback is welcome!</span>
      </div>

      {/* <button onClick={addCustomReport}>KEKEC</button> */}
      {options !== null ? (
        <div style={{ paddingTop: '50px' }}>
          {customReportsList !== undefined && customReportsList.length > 0 ? (
            <HighchartsReact
              highcharts={Highcharts}
              constructorType={'ganttChart'}
              options={o}
              allowChartUpdate={true}
              styledMode={true}
            />
          ) : customReportsList === undefined || customReportsList.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#666',
                fontSize: '16px',
                lineHeight: '1.5',
              }}
            >
              <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>There is currently no Custom Reports available</h3>
              <p style={{ margin: '0', fontSize: '14px' }}>
                {anyPublish
                  ? 'Please start by adding a new Custom Report'
                  : 'If you think this is a mistake, please get in touch with your agency contact'}
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div>
          {Array(20)
            .fill(0)
            .map((_, index) => (
              <Skeleton key={index} style={{ height: '35px', marginTop: index > 0 ? '10px' : '0' }} />
            ))}
        </div>
      )}
    </OrganisationReportStyled>
  );
};

export default OrganisationReport;

export const useGetCustomReports = (): readonly [(orgID: string) => Promise<CustomReportResponse[] | undefined>] => {
  const fetchCustomReports = async (orgID: string) => {
    const date15MonthsAgo = new Date();
    date15MonthsAgo.setMonth(date15MonthsAgo.getMonth() - 18);
    const startDate15MonthsAgo =
      date15MonthsAgo.getFullYear() + '-' + ('0' + (date15MonthsAgo.getMonth() + 1)).slice(-2) + '-' + '15';

    //TODO COOP
    const date6MonthsAgo = new Date();
    date6MonthsAgo.setMonth(date6MonthsAgo.getMonth() - 6);
    const startDate6MonthsAgo =
      date6MonthsAgo.getFullYear() + '-' + ('0' + (date6MonthsAgo.getMonth() + 1)).slice(-2) + '-' + '15';
    //TODO VATENFALL
    const date36MonthsAgo = new Date();
    date36MonthsAgo.setMonth(date36MonthsAgo.getMonth() - 36);
    const startDate36MonthsAgo =
      date36MonthsAgo.getFullYear() + '-' + ('0' + (date36MonthsAgo.getMonth() + 1)).slice(-2) + '-' + '15';
    try {
      const response = (await API.graphql({
        query: getCustomReport,
        variables: {
          Organization_id: orgID,
          From:
            orgID === 'CODK#DK' 
              ? startDate6MonthsAgo
              : orgID === 'SIL2#DK' || orgID === 'PD01#SE' || orgID === 'ORKL#DK'
                ? startDate36MonthsAgo
                : startDate15MonthsAgo,
        } as GetCustomReportQueryVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetCustomReportQuery };
      const res = response.data.getCustomReport;
      // console.log('RESPONSE', response.data.getCustomReport);
      if (res === null || res === undefined || res.length === 0) {
        return undefined;
      } else {
        const filteredResponse = res.filter((report): report is CustomReportResponse => report !== null);
        return filteredResponse;
      }
    } catch (e) {
      console.error('ERROR fetching CUSTOM REPORTS', e);
      return undefined;
    }
  };

  return [fetchCustomReports];
};

export const useSaveCustomReports = (): readonly [(obj: CampaignPeriodValues) => Promise<boolean>] => {
  const { activeCampaign } = useCampaignContext();
  const createCustomReport = async (obj: CampaignPeriodValues) => {
    const filters = {
      column: 'date',
      filter: {
        filtertype: filtertype.BETWEEN,
        datatype: datatype.DATE,
        from: obj.startDate,
        to: obj.endDate,
      },
    };

    try {
      const response = (await API.graphql({
        query: saveCustomReport,
        variables: {
          Client: {
            Code: activeCampaign?.ClientCode,
            Country: activeCampaign?.ClientCountry,
          },
          CampaignID: activeCampaign?.campaignId,
          CustomReportName: obj.customReportName,
          Filters: [filters],
        } as SaveCustomReportMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: SaveCustomReportMutation };
      console.log('RESPONSE', response);
      if (response.data.saveCustomReport) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      console.error('ERROR SAVING CUSTOM REPORT', e);
      return false;
    }
  };
  return [createCustomReport];
};

export const useUpdateCustomReports = () => {
  const { activeCampaign } = useCampaignContext();
  const updateCustomRep = async (values: { customReportName?: string; startDate?: string; endDate?: string }) => {
    try {
      const response = (await API.graphql({
        query: updateCustomReport,
        variables: {
          CustomReportID: activeCampaign?.campaignId,
          CustomReportName: values.customReportName,
          Filters: [
            {
              column: 'date',
              filter: {
                filtertype: filtertype.BETWEEN,
                datatype: datatype.DATE,
                from: values.startDate,
                to: values.endDate,
              },
            },
          ],
        } as UpdateCustomReportMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: UpdateCustomReportMutation };
      console.log('RESPONSE', response);
      return true;
    } catch (e) {
      console.error('ERROR UPDATING CUSTOM REPORT', e);
      return false;
    }
  };
  return [updateCustomRep];
};

export const useDeleteCustomReports = () => {
  //TODO unpublish custom report
  //change STATUS
  // call API
  const { activeCampaign } = useCampaignContext();
  const deleteCustomRep = async () => {
    try {
      const response = (await API.graphql({
        query: deleteCustomReport,
        variables: {
          ID: activeCampaign?.campaignId,
        } as DeleteCustomReportMutationVariables,
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: DeleteCustomReportMutation };
      console.log('RESPONSE', response);
      return true;
    } catch (e) {
      console.error('ERROR DELETING CUSTOM REPORT', e);
      return false;
    }
  };
  return [deleteCustomRep];
};
